// src/admin/OrganizationManagementPage.tsx
import { useAuth } from '@/auth/useAuth';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import {
  CreateOrganizationData,
  useCreateOrganization,
  useDeleteOrganization,
  useOrganizations,
  useRestoreOrganization,
} from '@/organization/useOrganizations';
import { Organization } from '@/types/organization';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Paper,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { CreateOrganizationForm } from './admin/CreateOrganizationForm';
import { DepartmentsList } from './admin/DepartmentsList';
import { EditOrganizationDialog } from './admin/EditOrganizationDialog';
import { OrganizationSettingsDialog } from './admin/OrganizationSettingsDialog';
import { OrganizationsList } from './admin/OrganizationsList';
import { OrganizationUsersDialog } from './admin/OrganizationUsersDialog';
import { UsersList } from './admin/UsersList';

// TabPanel component to handle tab content
function TabPanel(props: {
  children?: React.ReactNode;
  index: number;
  value: number;
}) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role={'tabpanel'}
      hidden={value !== index}
      id={`admin-tabpanel-${index}`}
      aria-labelledby={`admin-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

export function OrganizationManagementPage() {
  const { user, loading: authLoading } = useAuth();
  const [tabValue, setTabValue] = useState(0);
  const [selectedOrgId, setSelectedOrgId] = useState('');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [deleteOrg, setDeleteOrg] = useState<Organization | null>(null);
  const [editOrg, setEditOrg] = useState<Organization | null>(null);
  const [usersDialogOrg, setUsersDialogOrg] = useState<Organization | null>(
    null,
  );
  const [settingsDialogOrg, setSettingsDialogOrg] =
    useState<Organization | null>(null);

  // Fetch organizations
  const {
    data: organizations,
    isLoading: orgsLoading,
    error: orgsError,
  } = useOrganizations(true); // Include inactive organizations for admin view

  // Mutations
  const createOrganization = useCreateOrganization();
  const deleteOrganization = useDeleteOrganization();
  const restoreOrganization = useRestoreOrganization();

  // Restore handler
  const handleRestoreOrganization = async (org: Organization) => {
    if (!org?.id) return;
    try {
      await restoreOrganization.mutateAsync(String(org.id));
      await queryClient.invalidateQueries({ queryKey: ['organizations'] });
      await queryClient.refetchQueries({ queryKey: ['organizations'] });
    } catch (error) {
      console.error('Failed to restore organization:', error);
    }
  };

  // Loading state
  const isLoading = authLoading || orgsLoading;

  // Handle tab change
  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Handle organization creation
  const handleCreateOrganization = async (data: CreateOrganizationData) => {
    try {
      await createOrganization.mutateAsync(data);
      setCreateDialogOpen(false);
    } catch (error) {
      console.error('Failed to create organization:', error);
    }
  };

  // Handle organization deletion
  const queryClient = useQueryClient();
  const handleDeleteOrganization = async () => {
    if (!deleteOrg?.id) return;

    try {
      await deleteOrganization.mutateAsync(String(deleteOrg.id));
      setDeleteOrg(null);
      // Ensure organizations list is refreshed immediately
      await queryClient.invalidateQueries({ queryKey: ['organizations'] });
      await queryClient.refetchQueries({ queryKey: ['organizations'] });
    } catch (error) {
      console.error('Failed to delete organization.');
      console.error(error);
      if (error instanceof Error) {
        console.error(error.message);
      }
    }
  };

  // Show loading spinner
  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Check user permissions
  if (!user?.isSuperuser) {
    return (
      <Container maxWidth={'lg'} sx={{ mt: 4 }}>
        <Alert severity={'error'}>
          You do not have permission to access this page.
        </Alert>
      </Container>
    );
  }

  // Show error if organizations failed to load
  if (orgsError) {
    return (
      <Container maxWidth={'lg'} sx={{ mt: 4 }}>
        <Alert severity={'error'}>
          Failed to load organizations. Please try again later.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth={'lg'} sx={{ mt: 4 }}>
      <Paper sx={{ p: 4 }}>
        <Typography variant={'h4'} gutterBottom>
          Organization Management
        </Typography>

        <Typography variant={'body1'} color={'text.secondary'} sx={{ mb: 4 }}>
          Manage organizations, departments, and user assignments
        </Typography>

        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label={'Organizations'} />
          <Tab label={'Departments'} />
          <Tab label={'Users'} />
        </Tabs>

        <TabPanel value={tabValue} index={0}>
          <Box sx={{ mb: 3 }}>
            <Button
              variant={'contained'}
              color={'primary'}
              onClick={() => setCreateDialogOpen(true)}
            >
              Create Organization
            </Button>
          </Box>

          <OrganizationsList
            organizations={organizations ?? []}
            selectedOrgId={selectedOrgId}
            onSelectOrganization={setSelectedOrgId}
            onDeleteOrganization={setDeleteOrg}
            onEditOrganization={setEditOrg}
            onManageUsers={setUsersDialogOrg}
            onSettings={setSettingsDialogOrg}
            onRestoreOrganization={(org) => {
              void handleRestoreOrganization(org);
            }}
          />
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <DepartmentsList organizationId={selectedOrgId} />
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <UsersList organizationId={selectedOrgId} />
        </TabPanel>
      </Paper>

      {/* Create Organization Dialog */}
      <CreateOrganizationForm
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onSubmit={handleCreateOrganization}
        isLoading={createOrganization.isPending}
      />

      {/* Edit Organization Dialog */}
      <EditOrganizationDialog
        organization={editOrg}
        onClose={() => setEditOrg(null)}
      />

      {/* Organization Users Dialog */}
      <OrganizationUsersDialog
        organization={usersDialogOrg}
        onClose={() => setUsersDialogOrg(null)}
      />

      {/* Organization Settings Dialog */}
      <OrganizationSettingsDialog
        organization={settingsDialogOrg}
        onClose={() => setSettingsDialogOrg(null)}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={!!deleteOrg}
        title={'Delete Organization'}
        message={`Are you sure you want to delete ${deleteOrg?.name}? This action cannot be undone.`}
        onConfirm={() => void handleDeleteOrganization()}
        onCancel={() => setDeleteOrg(null)}
        confirmText={'Delete'}
        confirmColor={'error'}
      />
    </Container>
  );
}
